import * as Effects from 'redux-saga/effects';
import * as apiService from './service';
import {
    ensureReconciliations,
    fetchReconciliationsSucceeded,
    fetchReconciliationsFailed,
    ensureDrillDown,
    fetchDrillDownSucceeded,
    fetchDrillDownFailed,
} from './reconciliationsSlice';
import { selectReconciliationsEntry, selectDrillDownEntry } from './selectors';

const call: any = Effects.call;
const put = Effects.put;
const select: any = Effects.select;
const takeEvery = Effects.takeEvery;

const UNIFIER_SERVICE_KEY = 'UW_GET_RECONCILIATIONS';

function unwrapReconciliations(body: any) {
    return body?.responseBody?.[UNIFIER_SERVICE_KEY]?.data?.data;
}

// The reducer claims the entry (loading + token) on dispatch, but takeEvery spawns one worker
// per action regardless of that outcome — so the worker itself must re-check the claim before
// fetching. Otherwise every mounted consumer (chip + up to 4 pills + drawer) fires its own
// network call, defeating the "one fetch per company/year-set" contract (A-62).
function* ensureReconciliationsSaga(action: ReturnType<typeof ensureReconciliations>) {
    const { orgId, fys, requestToken } = action.payload;
    const entry = yield select(selectReconciliationsEntry, orgId, fys);
    if (!entry || entry.token !== requestToken) return;
    try {
        const body = yield call(apiService.fetchReconciliations, orgId, fys, null);
        const payload = unwrapReconciliations(body);
        // §4.1 failure contract: a swallowed UW failure surfaces as responseCode 20 + an empty
        // object, so responseCode/HTTP status alone is not sufficient — treat "no overall and
        // no sections" as failure so the chip/pills never render a 0% for an upstream outage.
        if (!payload?.overall && !payload?.sections?.length) {
            yield put(fetchReconciliationsFailed({ orgId, fys, requestToken, error: 'Reconciliations unavailable' }));
            return;
        }
        yield put(fetchReconciliationsSucceeded({ orgId, fys, payload, requestToken }));
    } catch (err: any) {
        yield put(fetchReconciliationsFailed({ orgId, fys, requestToken, error: err?.message || 'Something went wrong' }));
    }
}

function* ensureDrillDownSaga(action: ReturnType<typeof ensureDrillDown>) {
    const { orgId, fys, reconId, fy, requestToken } = action.payload;
    const entry = yield select(selectDrillDownEntry, orgId, fys, reconId, fy);
    if (!entry || entry.token !== requestToken) return;
    try {
        const body = yield call(apiService.fetchReconciliations, orgId, fys, { reconId, fy });
        const payload = unwrapReconciliations(body);
        const rows = payload?.drillDown?.rows;
        if (!Array.isArray(rows)) {
            yield put(fetchDrillDownFailed({ orgId, fys, reconId, fy, requestToken, error: 'Drill-down unavailable' }));
            return;
        }
        yield put(fetchDrillDownSucceeded({ orgId, fys, reconId, fy, rows, requestToken }));
    } catch (err: any) {
        yield put(fetchDrillDownFailed({ orgId, fys, reconId, fy, requestToken, error: err?.message || 'Something went wrong' }));
    }
}

// takeEvery, NEVER takeLatest — takeLatest would cancel the in-flight default fetch when an
// explicit-FY ensure follows it, breaking the "both entries end success" de-dup contract
// (D26-7). De-dup is the reducer's claim (isClaimable) PLUS each worker's own re-check of it
// above — the watcher itself makes no dedup decision.
export default function* reconciliationsSagaWatcher() {
    yield takeEvery(ensureReconciliations.type, ensureReconciliationsSaga);
    yield takeEvery(ensureDrillDown.type, ensureDrillDownSaga);
}
